import { Link } from 'react-router-dom';
import { apiGet } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';

type FeatureVerdict = 'hot' | 'warm' | 'cold';

interface FeatureSummary {
  featureId: string;
  displayName: string;
  description: string | null;
  category: string | null;
  archived: boolean;
  adoption: number;
  retention: number;
  revenue: number;
  eventCount: number;
  verdict: FeatureVerdict;
}

const VERDICT_LABEL: Record<FeatureVerdict, string> = {
  hot: '🔥 Hot',
  warm: '🟡 Warm',
  cold: '🧊 Cold',
};

const VERDICT_TONE: Record<FeatureVerdict, 'good' | 'warn' | 'bad'> = {
  hot: 'good',
  warm: 'warn',
  cold: 'bad',
};

export function FeatureAnalyticsPage() {
  const { data, loading, error, reload } = useApiResource(
    () => apiGet<FeatureSummary[]>('/analytics/admin/features'),
    [],
  );

  const active = data?.filter((f) => !f.archived) ?? [];
  const archived = data?.filter((f) => f.archived) ?? [];

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Analytics</h1>
          <p>
            Every feature that has called its analytics hook shows up here automatically —
            adoption, retention, and revenue over the last 30 days, with a stay/kill temperature
            read. New features need no setup beyond calling the hook once.
          </p>
        </div>
        <button type="button" className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="empty-state">Loading feature analytics…</p>
      ) : error ? (
        <div className="error-banner">{error}</div>
      ) : !data || data.length === 0 ? (
        <p className="empty-state">
          No features have reported analytics yet. Call AnalyticsService.track() (backend) once
          from any feature to see it here.
        </p>
      ) : (
        <>
          <div className="card-grid">
            {active.map((feature) => (
              <FeatureCard key={feature.featureId} feature={feature} />
            ))}
          </div>

          {archived.length > 0 && (
            <>
              <h2 className="dashboard-section-title" style={{ marginTop: '2rem' }}>
                Archived
              </h2>
              <div className="card-grid">
                {archived.map((feature) => (
                  <FeatureCard key={feature.featureId} feature={feature} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function FeatureCard({ feature }: { feature: FeatureSummary }) {
  return (
    <Link
      to={`/analytics/${encodeURIComponent(feature.featureId)}`}
      className="form-card"
      style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
        <h2 style={{ fontSize: '1rem', margin: 0 }}>{feature.displayName}</h2>
        <StatusBadge label={VERDICT_LABEL[feature.verdict]} tone={VERDICT_TONE[feature.verdict]} />
      </div>
      {feature.category && (
        <p className="form-hint" style={{ margin: '0.25rem 0 0' }}>
          {feature.category}
        </p>
      )}
      <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        <Metric label="Adoption" value={feature.adoption} />
        <Metric label="Retention" value={`${feature.retention}%`} />
        <Metric label="Revenue" value={`$${feature.revenue.toFixed(2)}`} />
      </div>
    </Link>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="form-hint" style={{ margin: 0 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}
